import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck, RefreshCw, Inbox, FileText, User, Calendar } from 'lucide-react';
import ReviewWorkflowActions from './ReviewWorkflowActions';
import { getAssessmentReviewQueue } from '../../services/reviewWorkflowApi';

/**
 * Approver-facing queue of assessments awaiting a review decision (FECOA-1592).
 * Lists everything in the `in_review` state the current user can see, with
 * inline Approve / Deny actions. The backend enforces the approve permission
 * and the approver-!=-submitter guard, so this page is safe to show broadly.
 */
const currentUserId = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    return u?.id ?? u?.user_id ?? u?.pk ?? null;
  } catch {
    return null;
  }
};

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const ReviewQueuePage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const userId = currentUserId();

  // silent=true refreshes in the background (no spinner/error reset) for the
  // poll + focus refetches, so the queue stays current without flicker.
  const fetchQueue = useCallback(async (silent = false) => {
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const data = await getAssessmentReviewQueue();
      setItems(Array.isArray(data) ? data : data.results || []);
    } catch (e) {
      if (!silent) {
        setError('Could not load the review queue.');
        setItems([]);
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  // Keep the queue live: silent refetch every 30s and on tab focus, so newly
  // submitted assessments appear without a manual refresh.
  useEffect(() => {
    const tick = () => fetchQueue(true);
    const interval = setInterval(tick, 30000);
    const onFocus = () => {
      if (document.visibilityState === 'visible') tick();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [fetchQueue]);

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-primary-50 p-2.5 rounded-full">
            <ClipboardCheck className="w-6 h-6 text-primary-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Review Queue</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Assessments submitted for review and awaiting a decision.
            </p>
          </div>
        </div>
        <button
          onClick={fetchQueue}
          disabled={loading}
          className="px-3 py-1.5 text-sm font-medium text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* States */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm mb-4">
          {error}
        </div>
      )}

      {!loading && !error && items.length === 0 && (
        <div className="text-center py-16 text-gray-500 dark:text-gray-400">
          <Inbox className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p className="text-sm">Nothing is awaiting review right now.</p>
        </div>
      )}

      {loading && (
        <div className="text-center py-16 text-gray-400 text-sm">Loading review queue…</div>
      )}

      {/* List */}
      {!loading && items.length > 0 && (
        <div className="space-y-2">
          {items.map((item) => (
            <div
              key={item.uuid || item.id}
              className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between hover:shadow-sm transition-shadow"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="bg-blue-50 p-2 rounded-full flex-shrink-0">
                  <FileText className="w-4 h-4 text-blue-600" />
                </div>
                <div className="min-w-0">
                  <button
                    onClick={() => item.uuid && navigate(`/assessment-builder/${item.uuid}`)}
                    className="text-sm font-semibold text-gray-900 dark:text-gray-100 hover:text-primary-600 truncate text-left"
                    title="Open assessment"
                  >
                    {item.name || 'Untitled Assessment'}
                  </button>
                  <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                    <span className="flex items-center gap-1">
                      <User className="w-3.5 h-3.5" />
                      {item.submitted_by_email || 'Unknown submitter'}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {formatDate(item.submitted_at)}
                    </span>
                  </div>
                </div>
              </div>

              <ReviewWorkflowActions
                entity={item}
                entityType="assessment"
                currentUserId={userId}
                isApprover
                onChanged={fetchQueue}
                onReview={(entity) =>
                  entity.uuid &&
                  navigate(`/assessment-builder/review/${entity.uuid}`, {
                    state: { reviewEntity: entity },
                  })
                }
                size="md"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewQueuePage;
